import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { AppState } from 'reducers/reducers';
import styles from './DemoLauncher.module.css';

// Feature-page icons, matched to the real spot navigation (see SpotMenu).
import BarChartIcon from 'assets/icons/barChart.svg?react';
import VideoCameraIcon from 'assets/icons/videoCamera.svg?react';
import PropertyIcon from 'assets/icons/property.svg?react';
import HouseIcon from 'assets/icons/house.svg?react';
import KeyIcon from 'assets/icons/key.svg?react';
import SmileIcon from 'assets/icons/smile.svg?react';
import CarIcon from 'components/Icons/CarIcon';
import LocationIcon from 'components/Icons/LocationIcon';
import ArrowRightIcon from 'components/Icons/ArrowRightIcon';
import ArrowSquareOutIcon from 'components/Icons/ArrowSquareOutIcon';

type Shortcut = {
    title: string; // demo-safe feature label — always shown
    desc: string; // generic, demo-safe description — shown in demo mode (compact)
    propertyName: string; // revealed only when demo mode is off (sales context)
    cityState: string; // revealed only when demo mode is off
    mapsUrl: string; // Google Maps link — revealed only when demo mode is off
    why: string; // why this property is a good representation — demo off only
    icon: ReactNode;
    color: string;
    href: string;
    external?: boolean; // opens a separate app in a new tab (can't honor demo mode)
    maps?: boolean; // the link itself is a Google Maps listing
};

// Each shortcut opens a demo property pre-loaded to showcase one feature.
// Demo mode ON  → compact launcher (clean for a live call).
// Demo mode OFF → sales-context card revealing the property, location, Maps
//                 link, and why it's a strong example of the feature.
const SHORTCUTS: Shortcut[] = [
    {
        title: 'Visibility',
        desc: 'ALPR camera coverage, capture rate, and the live driver log of every plate read.',
        propertyName: 'Washington Hill Parking',
        cityState: 'Baltimore, MD',
        mapsUrl: 'https://maps.app.goo.gl/kKTey4xFexj9f3aJA',
        why: 'Fully camera-equipped street lot with high plate-read volume — the driver log fills up fast, so the ALPR feature has plenty of live data to show.',
        icon: <VideoCameraIcon />,
        color: 'orange',
        href: '/organization/674/spots/1030/visibility',
    },
    {
        title: 'Revenue',
        desc: 'Month-over-month growth, revenue split by rental type, and performance against prior operators.',
        propertyName: 'Washington Hill Parking',
        cityState: 'Baltimore, MD',
        mapsUrl: 'https://maps.app.goo.gl/kKTey4xFexj9f3aJA',
        why: 'Taken over from a prior operator with clean before/after numbers and a healthy mix of hourly and monthly rentals — ideal for the growth story.',
        icon: <BarChartIcon />,
        color: 'green',
        href: '/organization/674/spots/1030/revenue',
    },
    {
        title: 'Overview',
        desc: 'The at-a-glance snapshot — live activity, occupancy, and earnings in one view.',
        propertyName: 'Church & Carriage Parking',
        cityState: 'Charleston, SC',
        mapsUrl: 'https://maps.app.goo.gl/SJypmExsWh6SjAop8',
        why: 'Transient-heavy location with steady hourly turnover — the overview shows live activity and earnings updating throughout the day.',
        icon: <HouseIcon />,
        color: 'indigo',
        href: '/organization/642/spots/987/detail',
    },
    {
        title: 'Local Intelligence',
        desc: 'Demand signals, nearby events, and competitive pricing in the area.',
        propertyName: 'Liberty Lot',
        cityState: 'Seattle, WA',
        mapsUrl: 'https://maps.app.goo.gl/UESJDkSmE8cf7NGr7',
        why: 'Dense downtown block with frequent events and several competing lots nearby — rich demand signals and pricing comparisons to demo.',
        icon: <LocationIcon />,
        color: 'blue',
        href: '/organization/826/spots/1258/local-intelligence',
    },
    {
        title: 'Property Insights',
        desc: 'Occupancy trends, utilization, and performance analytics for the location.',
        propertyName: '253 W 11th Garage',
        cityState: 'Wilmington, DE',
        mapsUrl: 'https://maps.app.goo.gl/wRuoYve5qSbBmmWDA',
        why: 'Garage with meaningful occupancy and utilization patterns across the day — the analytics charts have clear trends to walk through.',
        icon: <PropertyIcon />,
        color: 'violet',
        href: '/organization/723/spots/1114/property-insights',
    },
    {
        title: 'Tenants',
        desc: 'Manage tenants, see payment status, and track spaces paid for versus utilization.',
        propertyName: '253 W 11th Garage',
        cityState: 'Wilmington, DE',
        mapsUrl: 'https://maps.app.goo.gl/wRuoYve5qSbBmmWDA',
        why: 'Building with many monthly tenants in mixed payment states — clearly shows spaces paid for versus actual utilization.',
        icon: <CarIcon />,
        color: 'amber',
        href: '/organization/723/spots/1114/drivers?tab=tenants',
    },
    {
        title: 'Tenant Portal',
        desc: 'The tenant-facing portal experience — invoices, access, and account.',
        propertyName: 'eBay Tenant Account',
        cityState: 'San Jose, CA',
        mapsUrl: 'https://maps.app.goo.gl/ALy2e2f5mktSB3bGA',
        why: 'A recognizable enterprise tenant account — great for showing the full tenant-facing portal end to end.',
        icon: <KeyIcon />,
        color: 'teal',
        href: 'https://pay.airgarage.com/tenants/4Xvb9zrw3vtHjKrhSC6LWF/bulk-rentals/4cf7c232-6750-49d2-bfff-fb12d1524f50/admin',
        external: true,
    },
    {
        title: 'Google Reviews',
        desc: 'Open the live Google Maps listing to show ratings and driver reviews.',
        propertyName: '350 Beach Street',
        cityState: 'San Francisco, CA',
        mapsUrl: 'https://maps.app.goo.gl/ioGB7Nujz23AFyXo6',
        why: 'Strong public rating with recent driver reviews — a solid reputation proof point to pull up live.',
        icon: <SmileIcon />,
        color: 'crimson',
        href: 'https://maps.app.goo.gl/ioGB7Nujz23AFyXo6',
        external: true,
        maps: true,
    },
];

function tileStyle(color: string): React.CSSProperties {
    return {
        ['--tile-bg' as string]: `var(--ag-${color}-3)`,
        ['--tile-fg' as string]: `var(--ag-${color}-11)`,
    };
}

function open(href: string): void {
    window.open(href, '_blank', 'noopener,noreferrer');
}

// Carry demo mode into the opened page via a query param so the target applies
// the demo-mode blur on its sensitive data.
function demoHref(href: string): string {
    const sep = href.includes('?') ? '&' : '?';
    return `${href}${sep}demoMode=true`;
}

// The link to open for a shortcut. Internal demos always carry demoMode=true so
// the opened tab blurs by default. External links (other apps) can't honor it.
const launchHref = (s: Shortcut) => (s.external ? s.href : demoHref(s.href));

const goLabel = (s: Shortcut) => (s.maps ? 'Open in Google Maps' : 'Open demo');

// External links open a new tab → "open in new tab" icon; demos → right arrow.
const GoArrow = ({ shortcut }: { shortcut: Shortcut }) =>
    shortcut.external ? <ArrowSquareOutIcon /> : <ArrowRightIcon />;

// Demo mode ON — compact launcher card.
function CompactCard({ shortcut }: { shortcut: Shortcut }): JSX.Element {
    return (
        <a
            className={styles.shortcut}
            href={launchHref(shortcut)}
            target="_blank"
            rel="noopener noreferrer"
            style={tileStyle(shortcut.color)}
        >
            {shortcut.external && (
                <span className={styles.ext}>
                    <ArrowSquareOutIcon />
                </span>
            )}
            <div className={styles.tile}>{shortcut.icon}</div>
            <div className={styles.prop}>{shortcut.title}</div>
            <div className={styles.desc}>{shortcut.desc}</div>
            <div className={styles.go}>
                {goLabel(shortcut)}
                <span className={styles.arrow}>
                    <GoArrow shortcut={shortcut} />
                </span>
            </div>
        </a>
    );
}

// Demo mode OFF — sales-context card. Rendered as a div so the inner Maps
// link is valid markup (an anchor can't nest inside an anchor).
function ContextCard({ shortcut }: { shortcut: Shortcut }): JSX.Element {
    return (
        <div
            className={`${styles.shortcut} ${styles.context}`}
            role="link"
            tabIndex={0}
            onClick={() => open(launchHref(shortcut))}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') open(launchHref(shortcut));
            }}
            style={tileStyle(shortcut.color)}
        >
            <div className={styles.ctxHead}>
                <div className={styles.tile}>{shortcut.icon}</div>
                <span className={styles.featurePill}>{shortcut.title}</span>
            </div>
            <div className={styles.propName}>{shortcut.propertyName}</div>
            {shortcut.cityState && (
                <a
                    className={styles.loc}
                    href={shortcut.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    <LocationIcon /> {shortcut.cityState}
                    <ArrowSquareOutIcon className={styles.locExt} />
                </a>
            )}
            <div className={styles.why}>
                <span className={styles.whyLabel}>Why this location</span>
                {shortcut.why}
            </div>
            <div className={styles.go}>
                {goLabel(shortcut)}
                <span className={styles.arrow}>
                    <GoArrow shortcut={shortcut} />
                </span>
            </div>
        </div>
    );
}

type DemoLauncherProps = ReturnType<typeof mapStateToProps>;

function DemoLauncher({ isDemoMode }: DemoLauncherProps): JSX.Element {
    return (
        <div className={styles.root}>
            <div className={styles.content}>
                <div className={styles.head}>
                    <span className={`${styles.eyebrow} ${isDemoMode ? '' : styles.eyebrowCtx}`}>
                        {isDemoMode ? 'Demo mode' : 'Sales context'}
                    </span>
                    <h2>{isDemoMode ? 'Jump into a property demo' : 'Property context for your demo'}</h2>
                </div>

                <div className={styles.sectionLabel}>{isDemoMode ? 'Property demos' : 'Demo properties'}</div>
                <div className={styles.grid}>
                    {SHORTCUTS.map((shortcut) =>
                        isDemoMode ? (
                            <CompactCard key={`${shortcut.propertyName}-${shortcut.title}`} shortcut={shortcut} />
                        ) : (
                            <ContextCard key={`${shortcut.propertyName}-${shortcut.title}`} shortcut={shortcut} />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state: AppState) {
    return {
        isDemoMode: state.dashboard.isDemoMode,
    };
}

export default connect(mapStateToProps)(DemoLauncher);
